import { Button } from "@aix/ui"
import { X, RefreshCw } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { trpc } from "../../../lib/trpc"

interface AdjustPlanModalProps {
  weeklyPlanId: string | null
  coachName: string
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function AdjustPlanModal({ weeklyPlanId, coachName, isOpen, onClose, onSuccess }: AdjustPlanModalProps) {
  const [userFeedback, setUserFeedback] = useState("")

  const adjustMutation = trpc.plans.weeklyPlans.adjust.useMutation({
    onSuccess: (data) => {
      toast.success("Plan adjusted successfully!")
      if (data.coachMessage) {
        setTimeout(() => {
          toast.success(data.coachMessage, { duration: 5000 })
        }, 500)
      }
      onSuccess()
      onClose()
      setUserFeedback("")
    },
    onError: (error) => {
      toast.error(error.message || "Failed to adjust plan")
    },
  })

  const handleSubmit = () => {
    if (!userFeedback.trim()) {
      toast.error("Please provide feedback on why you need to adjust this plan")
      return
    }

    if (!weeklyPlanId) {
      toast.error("No plan selected")
      return
    }

    adjustMutation.mutate({
      weeklyPlanId,
      userFeedback,
    })
  }

  if (!isOpen || !weeklyPlanId) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <RefreshCw className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Adjust Weekly Plan</h2>
            </div>
            <p className="text-sm text-muted-foreground">Let {coachName} know why this plan needs adjustment</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Info Banner */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <RefreshCw className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1">How Plan Adjustment Works</h3>
                <p className="text-sm text-muted-foreground">
                  Tell {coachName} what&apos;s changed or what isn&apos;t working. AI will analyze your feedback along
                  with your past weeks&apos; performance and adjust the plan to better fit your current situation.
                </p>
              </div>
            </div>
          </div>

          {/* Feedback Area */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Your Feedback</label>
            <textarea
              value={userFeedback}
              onChange={(e) => setUserFeedback(e.target.value)}
              placeholder="Example: I'm feeling overwhelmed this week and need to reduce my workload... OR: I have more capacity than expected, can we add more challenges?... OR: Something unexpected came up and I need to shift priorities..."
              className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-primary resize-none"
              rows={10}
            />
            <p className="text-xs text-muted-foreground mt-2">
              {userFeedback.length} characters • Be specific about what needs to change
            </p>
          </div>

          {/* Tips */}
          <div className="bg-secondary rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-2 text-sm">Tips for better adjustments:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Explain what&apos;s changed in your situation</li>
              <li>• Be honest about your energy levels and capacity</li>
              <li>• Mention if something in the original plan isn&apos;t realistic</li>
              <li>• Include any new priorities or constraints</li>
            </ul>
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Adjusting the plan will keep your original plan saved and create a new adjusted
              version based on your feedback. You&apos;ll be able to see both versions.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={adjustMutation.isLoading}
            disabled={adjustMutation.isLoading || !userFeedback.trim()}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Adjust Plan
          </Button>
        </div>
      </div>
    </div>
  )
}
